<script setup>

import { entwicklung } from '@/client';
import { client } from '@/lohnrechner';
import { useLohndatenStore, useResultStore } from '@/stores/lohndaten';
import { ref } from 'vue';

import numeral from 'numeral'
import { onMounted } from 'vue';

import { Chart as ChartJS, registerables } from 'chart.js';

import format from 'string-format'
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(...registerables);
ChartJS.register(annotationPlugin);

ChartJS.defaults.color = '#34333C';
ChartJS.defaults.font.size = 16;
ChartJS.defaults.font.family = 'Theinhardt, sans-serif';
import { Line } from 'vue-chartjs';
import { computed } from 'vue';
import { inject } from 'vue';

const t = inject('t')

const props = defineProps({
    showYearly: {
        type: Boolean,
        default: false
    },
    factor: {
        type: Number,
        default: 1
    }
})

const store = useLohndatenStore()
const storeResult = useResultStore()

let values = ref(null)

async function fetchEntwicklung() {

    let response = await entwicklung({
        body: {

            beruf: store.beruf,
            branche: store.branche,
            age: store.age,
            experience: Math.min(store.experience, store.age - 18),
            position: store.stellung,
            education: store.ausbildung,
            plz: store.onrp,
            stunden: store.stunden,
            first_name: store.contact.first_name,
            last_name: store.contact.last_name,
            email: store.contact.email
        },
        client: client
    });

    if (response.error) {
        return
    }

    values.value = response.data

}

const startAge = computed(() => {
    return storeResult.request.age - storeResult.request.experience
});

function validRanges() {
    if (!values.value) {
        return false
    }
    let userAge = startAge.value;
    let minAge = storeResult.request.education.min_age || 18;
    return values.value.ages.some((age) => age >= minAge && age <= userAge)
}

function getRanges() {

    let userAge = startAge.value
    let minAge = storeResult.request.education.min_age || 18;


    const ages = values.value.ages.filter((value) => value >= minAge);

    const ageFilter = (value, idx) => {
        return values.value.ages.at(idx) >= minAge;
    };
    const p25 = values.value.p25.filter(ageFilter)
    const p75 = values.value.p75.filter(ageFilter)
    const p50 = values.value.p50.filter(ageFilter);
    return {
        ages,
        p25,
        p75,
        p50,
        userAge
    }
}

const ageIdx = computed(() => {
    if (!values.value) {
        return null
    }
    let { ages } = getRanges();

    return ages.findIndex((age) => age === storeResult.request.age);
})

const startAgeIdx = computed(() => {
    if (!values.value) {
        return null
    }
    let { ages } = getRanges();

    return ages.findIndex((age) => age === startAge.value);
})

const data = computed(() => {
    if (!values.value) {
        return null
    }

    let { ages, p25, p75, p50, userAge } = getRanges();

    return values.value ? {
        labels: ages,
        datasets: [
            {
                label: 'p50-above',
                data: p50.map((value, idx) => ages.at(idx) >= userAge ? props.factor * value : null),
                tension: 0.5,
                borderWidth: () => window.innerWidth < 600 ? 6 : 10,
                borderColor: '#34333C',
                backgroundColor: '#34333C',
                pointStyle: false,
            },
            {
                label: 'p50-below',
                data: p50.map((value, idx) => ages.at(idx) <= userAge ? props.factor * value : null),
                tension: 0.5,
                borderWidth: () => window.innerWidth < 600 ? 6 : 10,
                borderColor: '#34333C',
                backgroundColor: '#34333C',
                borderDash: [10, 10],
                pointStyle: false,
            },
            {
                label: null,
                data: p75.map((value) => props.factor * value),
                tension: 0.5,
                borderWidth: 0,
                borderColor: '#BD9999',
                backgroundColor: '#BD9999',
                pointStyle: false,
                fill: false,
            },
            {
                label: 'band',
                data: p25.map((value) => props.factor * value),
                borderWidth: 0,
                borderColor: 'transparent',
                backgroundColor: '#76c97a88',
                pointStyle: false,
                tension: 0.5,

                fill: '-1',
            },
        ]
    } : null
})



onMounted(() => {
    fetchEntwicklung()
})

store.$subscribe(() => {
    setTimeout(() => {
        // console.log('store changed, fetching entwicklung')
        fetchEntwicklung()
    }, 10)
})

const options = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
        padding: 0
    },
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            enabled: false,
        },
        annotation: {
            annotations: {
                line1: {
                    type: 'line',
                    mode: 'vertical',
                    scaleID: 'x',
                    value: ageIdx.value, // or index if using labels
                    borderColor: '#bd9999',
                    borderWidth: 2,
                    label: {
                        enabled: true,
                        position: 'bottom'
                    }
                }
            }
        }
    },
    scales: {
        y: {
            beginAtZero: false,
            ticks: {
                callback: function (value) {

                    return numeral(value).format('0,0').replace(',', '’') + ' CHF'
                },
                font: {
                    size: () => window.innerWidth < 600 ? 14 : 18,
                },
                padding: () => window.innerWidth < 600 ? 12 : 20,

            },
            title: {
                display: false,
                text: props.showYearly ? t("jahreslohn") : t('monatslohn'),
            },
            border: {
                display: false,
            },
            grid: {
                lineWidth: 0.5,
                color: "#34333C"
            }

        },
        x: {
            ticks: {
                font: {
                    size: () => window.innerWidth < 600 ? 14 : 16,
                },
                maxRotation: 0,
                callback: function (value) {
                    let v = this.getLabelForValue(value)
                    let spacing = window.innerWidth < 600 ? 10 : 5
                    if (v % spacing == 0) {
                        return v
                    }
                    return ''
                },

                autoSkip: false,
            },
            title: {
                display: true,
                text: t('alter')
            },
            grid: {
                display: false
            }

        }
    }
}))

const chart$ = ref(null)

function onLegendClick(dataset) {
    if (!chart$.value) {
        return;
    }
    const chart = chart$.value.chart;
    const datasets = chart.data.datasets;
    datasets.forEach((ds) => {
        if (ds.label === dataset) {
            ds.hidden = !ds.hidden;
        }
    });
    toggleVisibility.value = false;
    toggleVisibility.value = true;

    chart.update();
}

const toggleVisibility = ref(true);

function isEnabled(dataset) {
    if (!chart$.value) {
        return true;
    }

    if (!chart$.value.chart) {
        return true;
    }
    const chart = chart$.value.chart;
    const datasets = chart.data.datasets;
    const ds = datasets.find((ds) => ds.label === dataset);
    return toggleVisibility.value && !ds.hidden;
}

function _round(value, to = 10) {
    let divisor = 1 / to
    let rounded = Math.round(value * divisor) * to
    return rounded
}

const hasBelow = computed(() => {
    if (!values.value) {
        return null
    }
    let { ages, userAge } = getRanges();

    return ages.at(0) < userAge;

});

const hasAbove = computed(() => {
    if (!values.value) {
        return null
    }
    let { ages, userAge } = getRanges();

    return ages.at(-1) > userAge;
});

const lohnText = computed(() => {
    if (!values.value) {
        return ''
    }
    const formatSalary = (value) => {
        const roundedValue = _round(value || 0) * props.factor;
        return numeral(roundedValue).format('0,0').replace(',', '’');
    };
    let { p50 } = getRanges();

    const salaryData = {
        lohnNeu: formatSalary(p50?.[startAgeIdx.value]),
        lohnPensionierung: formatSalary(p50?.at(-1)),
        lohnJetzt: formatSalary(storeResult.result?.p50),
        alterJetzt: storeResult.request.age,
        anstellungsDauer: storeResult.request.experience,
        alterAntritt: startAge.value,
        lohnart: props.showYearly ? t('jahreslohn') : t('monatslohn'),
        jahrEN: storeResult.request.experience == 1 ? t('jahr') : t('jahren'),

    };

    if (store.experience > 0)
        return format(t('lohnentwicklung-text'), salaryData);

    return format(t('lohnentwicklung-text-0'), salaryData);

});

const title = computed(() => {
    return format(t("lohnentwicklung"), {
        lohnart: props.showYearly ? t('jahreslohn') : t('monatslohn'),
    })
})

</script>

<template>
    <section class="card no-print">
        <h3>{{ title }}</h3>
        <div class="entwicklung" v-if="data">
            <Line ref='chart$' :data="data" :options="options"></Line>
        </div>

        <section class="legend">
            <div class="legend-item" :class="{ disabled: !isEnabled('p50-below') }" @click="onLegendClick('p50-below')"
                v-if="hasBelow">
                <span class="legend-color p50-below"></span>
                <span class="legend-label">{{ format(t("percentile-50-below"), {
                    age: startAge
                }) }}</span>
            </div>
            <div class="legend-item" :class="{ disabled: !isEnabled('p50-above') }" @click="onLegendClick('p50-above')"
                v-if="hasAbove">
                <span class="legend-color p50-above"></span>
                <span class="legend-label">{{ format(t("percentile-50-above"), {
                    age: startAge
                }) }}</span>
            </div>
            <div class="legend-item" :class="{ disabled: !isEnabled('band') }" @click="onLegendClick('band')">
                <span class="legend-color band"></span>
                <span class="legend-label">{{ t("uebliche-loehne-chart") }}</span>
            </div>
            <div class="legend-item age">
                <span class="legend-color age"></span>
                <span class="legend-label">{{ format(t("age-chart"), { age: storeResult.request.age }) }}</span>
            </div>
        </section>
        <!-- <div @click="showMore = !showMore" class="toggle">
            <svg width="48" height="45" viewBox="0 0 48 45" fill="none" xmlns="http://www.w3.org/2000/svg"
                @click="showMorePopup = !showMorePopup">
                <circle cx="23.8018" cy="22.6419" r="21.5274" transform="rotate(-180 23.8018 22.6419)"
                    stroke-width="1.2" />
                <circle class="dot" cx="23.9708" cy="14.445" r="2.60976" transform="rotate(90 23.9708 14.445)" />
                <circle class="dot" cx="23.9708" cy="23.445" r="2.60976" transform="rotate(90 23.9708 23.445)" />
                <circle class="dot" cx="23.9708" cy="32.445" r="2.60976" transform="rotate(90 23.9708 32.445)" />
            </svg>

        </div> -->

        <!-- <div v-if="showMore" v-html="t('lohnentwicklung-text')"></div> -->
        <div v-html="lohnText" class="entwicklung-text"></div>
    </section>
</template>

<style scoped>
section.card {
    display: flex;
    flex-direction: column;
    padding: 2rem 2rem;
}

section.legend {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    margin-top: 0.2rem;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
}

.legend-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
}

.legend-color {
    width: 35px;
    height: 8px;
    display: inline-block;
}

.legend-color.p50-above {
    background-color: #34333C;
}

.legend-color.p50-below {
    background: repeating-linear-gradient(90deg, #34333C 0px, #34333C 5px, transparent 5px, transparent 10px);
}

.disabled {
    text-decoration: line-through;
}

.legend-color.band {
    background: #76c97a;
}

.legend-color.age {
    background: #bd9999;
    height: 20px;
    width: 5px;
    background-repeat: no-repeat;
    background-repeat: no-repeat;
}

.legend-item.age {
    cursor: default;
}

.toggle {
    cursor: pointer;
    width: 48px;
    height: 48px;
    align-self: flex-end;
}

.dot {
    fill: var(--color-text);
}

svg circle {
    stroke: var(--color-text);
}

section h3 {
    text-align: start;
    color: var(--color-text);
    margin-top: 0;
    margin-bottom: 2rem;
}

.entwicklung {
    position: relative;
    height: max(40vh, 420px);
    margin-left: -20px;
    margin-right: -20px;
}

.entwicklung-text {
    margin-top: 1.2rem;
}
</style>
<style>
@media print {
    .entwicklung canvas {
        width: 100% !important;
    }

}
</style>